// CommentsInfoRow.js
import React, { useState, useRef, useEffect } from 'react';
import { updateOrder } from '../../api';

const CommentsInfoRow = ({ order, readOnly = false }) => {
  // Estado para la edición de los comentarios
  const [isEditingInternalComment, setIsEditingInternalComment] = useState(false);
  const [isEditingExternalComment, setIsEditingExternalComment] = useState(false);
  const [isEditingDeliveryComment, setIsEditingDeliveryComment] = useState(false);

  // Comentarios actuales y sus setters
  const [internalComment, setInternalComment] = useState(order.internalComments || '');
  const [externalComment, setExternalComment] = useState(order.externalComments || '');
  const [deliveryComment, setDeliveryComment] = useState(order.deliveryComments || '');

  // Referencias para los comentarios
  const externalCommentRef = useRef(null);
  const internalCommentRef = useRef(null);
  const deliveryCommentRef = useRef(null);

  // Función para manejar la edición de comentarios
  const toggleEditComment = (e, commentType) => {
    if (readOnly) return;
    if (commentType === 'external') setIsEditingExternalComment(true);
    if (commentType === 'internal') setIsEditingInternalComment(true);
    if (commentType === 'delivery') setIsEditingDeliveryComment(true);
    e.stopPropagation();
  };

  // Función para manejar el clic fuera del área de edición
  const handleOutsideClick = (event, ref, commentType, currentValue, apiFieldName) => {
    if (ref.current && !ref.current.contains(event.target)) {
      handleClickOutsideComment(commentType, ref, currentValue, apiFieldName);
    }
  };

  // Función para manejar el cambio en los comentarios
  const handleChangeExternalComment = (e) => {
    setExternalComment(e.target.value);
  };

  const handleChangeInternalComment = (e) => {
    setInternalComment(e.target.value);
  };

  const handleChangeDeliveryComment = (e) => {
    setDeliveryComment(e.target.value);
  };

  // Función para guardar los comentarios al hacer clic fuera
  const handleClickOutsideComment = async (commentType, ref, currentValue, apiFieldName) => {
    if (commentType) {
      const updatedComment = ref.current.value.trim() === '' ? '' : ref.current.value;

      if (updatedComment !== currentValue) {
        await updateOrder(order.id, { [apiFieldName]: updatedComment, id: order.id });
      }

      if (commentType === 'external') setIsEditingExternalComment(false);
      if (commentType === 'internal') setIsEditingInternalComment(false);
      if (commentType === 'delivery') setIsEditingDeliveryComment(false);
    }
  };

  useEffect(() => {
    if (isEditingInternalComment) {
      const handleClick = (event) =>
        handleOutsideClick(event, internalCommentRef, 'internal', internalComment, 'internalComments');
      document.addEventListener('click', handleClick);
      return () => {
        document.removeEventListener('click', handleClick);
      };
    }
  }, [isEditingInternalComment]);

  useEffect(() => {
    if (isEditingExternalComment) {
      const handleClick = (event) =>
        handleOutsideClick(event, externalCommentRef, 'external', externalComment, 'externalComments');
      document.addEventListener('click', handleClick);
      return () => {
        document.removeEventListener('click', handleClick);
      };
    }
  }, [isEditingExternalComment]);

  useEffect(() => {
    if (isEditingDeliveryComment) {
      const handleClick = (event) =>
        handleOutsideClick(event, deliveryCommentRef, 'delivery', deliveryComment, 'deliveryComments');
      document.addEventListener('click', handleClick);
      return () => {
        document.removeEventListener('click', handleClick);
      };
    }
  }, [isEditingDeliveryComment]);

  return (
        <span>
          <div>
            <label>Comentarios Externos: </label>
            {!isEditingExternalComment ? (
              <label
                data-testid="external-comment-label"
                className="comment-editable"
                style={{ cursor: readOnly ? 'default' : 'pointer', fontWeight: 'normal', color: readOnly ? '#6c757d' : '#007bff', marginLeft: '4px' }}
                onClick={readOnly ? undefined : (e) => toggleEditComment(e, 'external')}
              >
                {externalComment || 'No hay comentarios externos'}
              </label>
            ) : (
              <textarea
                data-testid="external-comment-textarea"
                ref={externalCommentRef}
                value={externalComment}
                onChange={handleChangeExternalComment}
                className="input-field"
                rows="3"
                autoFocus
              />
            )}
          </div>

          <div>
            <label>Comentarios Internos: </label>
            {!isEditingInternalComment ? (
              <label
                data-testid="internal-comment-label"
                className="comment-editable"
                style={{ cursor: readOnly ? 'default' : 'pointer', fontWeight: 'normal', color: readOnly ? '#6c757d' : '#007bff', marginLeft: '4px' }}
                onClick={readOnly ? undefined : (e) => toggleEditComment(e, 'internal')}
              >
                {internalComment || 'No hay comentarios internos'}
              </label>
            ) : (
              <textarea
                data-testid="internal-comment-textarea"
                ref={internalCommentRef}
                value={internalComment}
                onChange={handleChangeInternalComment}
                className="input-field"
                rows="3"
                autoFocus
              />
            )}
          </div>

          <div>
            <label>Comentarios de Entrega:</label>
            {!isEditingDeliveryComment ? (
              <label
                data-testid="delivery-comment-label"
                className="comment-editable"
                style={{ cursor: readOnly ? 'default' : 'pointer', fontWeight: 'normal', color: readOnly ? '#6c757d' : '#007bff', marginLeft: '4px' }}
                onClick={readOnly ? undefined : (e) => toggleEditComment(e, 'delivery')}
              >
                {deliveryComment || 'No hay comentarios de entrega'}
              </label>
            ) : (
              <textarea
                data-testid="delivery-comment-textarea"
                ref={deliveryCommentRef}
                value={deliveryComment}
                onChange={handleChangeDeliveryComment}
                className="input-field"
                rows="3"
                autoFocus
              />
            )}
          </div>
        </span>
  );
};

export default CommentsInfoRow;
